// groupDeliverables — converts a flat Deliverable[] into the
// discriminated union the grouped DeliverableTable consumes. A row is
// one of: ParentRow (a recurring schedule with its children inlined),
// ChildRow (an occurrence with a back-reference to its parent),
// OneOffRow (a standalone deliverable), or OrphanRow (an occurrence
// whose parent schedule is missing from the input — typically because
// the parent was archived after children were generated).
//
// Orphans are surfaced as their own row kind so the table can render
// them with a distinct chip and the tab can mount a banner explaining
// the data inconsistency. Promoting them out of OneOffRow keeps the
// "graceful, don't drop data" invariant from earlier phases while
// making the orphan state loud rather than silent.
//
// Top-level ordering is deterministic, keyed on the row's primary
// due-signal:
//   - OneOffRow / OrphanRow → dueDate (undefined sorts to end)
//   - ParentRow             → earliest child occurrenceDate, falling
//                              back to recurrenceRule.startDate, then
//                              the empty string (which sorts to the
//                              front)
// Ties break on id so the output is stable across re-invocations with
// the same input in a different order (tested).

import type { Deliverable } from "../../../../types";

export interface ParentRow {
  kind: "parent";
  deliverable: Deliverable;
  children: ChildRow[];
  /**
   * When set (e.g. during an active summary filter), preserves the
   * full unfiltered child set so the OccurrenceStrip renders the
   * entire schedule history even though `children` is narrowed to
   * the matching rows.
   */
  allChildren?: ChildRow[];
}

export interface ChildRow {
  kind: "child";
  deliverable: Deliverable;
  parent: Deliverable;
}

export interface OneOffRow {
  kind: "one-off";
  deliverable: Deliverable;
}

export interface OrphanRow {
  kind: "orphan";
  deliverable: Deliverable;
  /** Parent id the deliverable references but which isn't in the input. */
  missingParentId: string;
}

export type Row = ParentRow | ChildRow | OneOffRow | OrphanRow;

function childSortKey(d: Deliverable): string {
  return d.occurrenceDate ?? "￿"; // undefined sorts to end
}

function parentSortKey(row: ParentRow): string {
  const earliestChild = row.children[0]?.deliverable.occurrenceDate;
  if (earliestChild) return earliestChild;
  return row.deliverable.recurrenceRule?.startDate ?? "";
}

function dueSortKey(d: Deliverable): string {
  return d.dueDate ?? d.occurrenceDate ?? "￿";
}

function topLevelSortKey(row: ParentRow | OneOffRow | OrphanRow): string {
  if (row.kind === "parent") return parentSortKey(row);
  return dueSortKey(row.deliverable);
}

export function groupDeliverables(deliverables: Deliverable[]): Row[] {
  const parentsById = new Map<string, Deliverable>();
  const oneOffs: Deliverable[] = [];
  const childrenByParentId = new Map<string, Deliverable[]>();

  for (const d of deliverables) {
    if (d.parentId) {
      const bucket = childrenByParentId.get(d.parentId) ?? [];
      bucket.push(d);
      childrenByParentId.set(d.parentId, bucket);
    } else if (d.recurrenceRule) {
      parentsById.set(d.id, d);
    } else {
      oneOffs.push(d);
    }
  }

  // Children whose parent isn't in the input become OrphanRows
  // rather than OneOffRows — the missing-parent pointer is preserved
  // for the banner / restore flow.
  const orphans: { deliverable: Deliverable; missingParentId: string }[] = [];
  for (const [parentId, kids] of childrenByParentId.entries()) {
    if (!parentsById.has(parentId)) {
      for (const kid of kids) {
        orphans.push({ deliverable: kid, missingParentId: parentId });
      }
      childrenByParentId.delete(parentId);
    }
  }

  const parentRows: ParentRow[] = Array.from(parentsById.values()).map(
    (parent) => {
      const kids = (childrenByParentId.get(parent.id) ?? [])
        .slice()
        .sort((a, b) => {
          const ak = childSortKey(a);
          const bk = childSortKey(b);
          if (ak < bk) return -1;
          if (ak > bk) return 1;
          return a.id < b.id ? -1 : 1;
        });
      return {
        kind: "parent" as const,
        deliverable: parent,
        children: kids.map((kid) => ({
          kind: "child" as const,
          deliverable: kid,
          parent,
        })),
      };
    },
  );

  const oneOffRows: OneOffRow[] = oneOffs.map((d) => ({
    kind: "one-off" as const,
    deliverable: d,
  }));

  const orphanRows: OrphanRow[] = orphans.map(
    ({ deliverable, missingParentId }) => ({
      kind: "orphan" as const,
      deliverable,
      missingParentId,
    }),
  );

  const topLevel: Array<ParentRow | OneOffRow | OrphanRow> = [
    ...parentRows,
    ...oneOffRows,
    ...orphanRows,
  ];
  topLevel.sort((a, b) => {
    const ak = topLevelSortKey(a);
    const bk = topLevelSortKey(b);
    if (ak < bk) return -1;
    if (ak > bk) return 1;
    return a.deliverable.id < b.deliverable.id ? -1 : 1;
  });

  return topLevel;
}
